import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { CheckCircle, Info, WarningCircle, X } from "@phosphor-icons/react";
import styles from "./Toast.module.css";

type ToastKind = "success" | "error" | "info";

type Toast = {
  id: number;
  kind: ToastKind;
  text: string;
};

type ToastContextValue = {
  push: (kind: ToastKind, text: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, text: string) => {
      const id = ++idRef.current;
      setToasts((current) => [...current, { id, kind, text }]);
      window.setTimeout(() => remove(id), kind === "error" ? 6000 : 3500);
    },
    [remove],
  );

  const value = useMemo<ToastContextValue>(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.stack} role="status" aria-live="polite">
        {toasts.map((t) => (
          <ToastCard key={t.id} toast={t} onDismiss={() => remove(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const Icon =
    toast.kind === "success" ? CheckCircle : toast.kind === "error" ? WarningCircle : Info;
  return (
    <div className={`${styles.toast} ${styles[toast.kind]}`}>
      <Icon size={18} weight="regular" className={styles.icon} />
      <span className={styles.text}>{toast.text}</span>
      <button type="button" className={styles.close} onClick={onDismiss} aria-label="Dismiss">
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback: kein Provider → no-op, damit Tests/Storybook nicht crashen
    return {
      success: () => undefined,
      error: () => undefined,
      info: () => undefined,
    };
  }
  return {
    success: (text: string) => ctx.push("success", text),
    error: (text: string) => ctx.push("error", text),
    info: (text: string) => ctx.push("info", text),
  };
}

// Kleine Helper-Hook: fängt Query-Error-Objekte ab und zeigt eine sinnvolle Nachricht.
export function useApiErrorToast() {
  const toast = useToast();
  return useCallback(
    (err: unknown, fallback: string) => {
      const e = err as { status?: number; message?: string };
      const msg = [e?.status ? `HTTP ${e.status}` : null, e?.message]
        .filter(Boolean)
        .join(" ") || fallback;
      toast.error(msg);
    },
    [toast],
  );
}

